/**
 * Vendor portal helpers — referral cookie, API calls.
 *
 * Referral attribution flow:
 *   1. On any page load, if `?ref=CODE` is in the URL, store it in a
 *      30-day cookie `facetra_ref` (per user-decision 2b).
 *   2. On signup (both Google + password paths), read the cookie and
 *      send it as `ref` in the request body — backend stamps the user's
 *      vendor_id.
 *   3. Attribution is locked at signup — later ref clicks by the same
 *      user do not overwrite.
 */
import { API } from "@/lib/api";
import axios from "axios";

const REF_COOKIE = "facetra_ref";
const REF_TTL_DAYS = 30;

/** Read a cookie value, safely. */
function getCookie(name) {
  if (typeof document === "undefined") return null;
  const m = document.cookie.match(new RegExp("(?:^|; )" + name + "=([^;]*)"));
  return m ? decodeURIComponent(m[1]) : null;
}

function setCookie(name, value, days) {
  const d = new Date();
  d.setTime(d.getTime() + days * 24 * 60 * 60 * 1000);
  // No `secure` on localhost dev; add on https at runtime.
  const secure = window.location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `${name}=${encodeURIComponent(value)}; expires=${d.toUTCString()}; path=/; SameSite=Lax${secure}`;
}

/**
 * Call once on page load — if a `?ref=CODE` is in the URL, drop the
 * 30-day cookie so the code survives navigation into signup.
 * Only writes the cookie if the code validates against the backend
 * (guards against poisoning our attribution with junk).
 */
export async function captureReferralFromUrl() {
  if (typeof window === "undefined") return null;
  try {
    const params = new URLSearchParams(window.location.search);
    const ref = (params.get("ref") || "").trim().toUpperCase();
    if (!ref) return getCookie(REF_COOKIE);
    // Validate before caching — an invalid code should not blow away a
    // previously-saved good one.
    const { data } = await axios.get(`${API}/vendor/public/referral/${ref}`);
    if (data?.valid) {
      setCookie(REF_COOKIE, ref, REF_TTL_DAYS);
      return ref;
    }
  } catch (_e) {
    // Silent — bad ref codes are not user-facing errors.
  }
  return getCookie(REF_COOKIE);
}

/** Read the currently-stored referral code (used on signup submission). */
export function getStoredReferralCode() {
  return getCookie(REF_COOKIE);
}

/** Clear the referral cookie — call after a successful signup so the
 *  code doesn't leak into a second unrelated account on the same device. */
export function clearReferralCookie() {
  document.cookie = `${REF_COOKIE}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
}
